import { AfterViewInit, Component, OnInit, ViewChild } from '@angular/core';
import { AppService, DentalBidirectionalListItem, DentalReferralForm, DentalReferralFormReview, GetDentalBidirectionalListsRequest, GetDentalReferralsToReprocessRequest, GetDentalReferralsToReprocessResponse, SaveDentalReferralReviewsRequest, ReprocessReferralRequest, PCPReferral } from '../app.service';
import { AlertService } from '../common/alert.service';
import { LoadingService } from '../common/loading.service';
import { MatDialog } from '@angular/material/dialog';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { DcoReferralDetailsDialogComponent } from '../dco-referrals/dco-referral-details-dialog/dco-referral-details-dialog.component';
import { MatSort, Sort } from '@angular/material/sort';
import { CommonFunctions } from '../common/common-functions';
import { MatPaginator } from '@angular/material/paginator';
import { DatePipe } from '@angular/common';
import { ReferralLockService } from '../common/referral-lock.service';
import { AuthService } from '../auth/services/auth.service';
import { ConfirmDialogComponent } from '../common/confirm-dialog/confirm-dialog.component';
import { ExportDialogData } from '../common/export/models';
import { ExportDialogComponent } from '../common/export/export-dialog/export-dialog.component';

@Component({
  selector: 'app-resolution-center',
  templateUrl: './resolution-center.component.html',
  styleUrl: './resolution-center.component.scss'
})
export class ResolutionCenterComponent implements OnInit, AfterViewInit {

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;

  public referrals: DentalReferralForm[] = [];
  displayedColumns: string[] = ['select', 'locked', 'memberId', 'memberName', 'receivedDate', 'source', 'reprocessReason', 'dcoNote', 'actions'];
  referralsDataSource: MatTableDataSource<DentalReferralForm> = new MatTableDataSource([]);

  public currentSearchValue: string = '';
  public currentFilterSelection: string[] = [];
  public currentActionValue: string = '';
  public filtersEnabled: boolean = true;

  // Initialize a SelectionModel to track the selected rows
  selection = new SelectionModel<DentalReferralForm>(true, []); // true for multi-selection
  selectedReferral: DentalReferralForm = null;

  //activeRow: number | null = null; // Tracks the active row index
  showClosed: boolean = false; // Default to show all referrals


  public dcoNotesListItems: DentalBidirectionalListItem[] = [];
  public reprocessReasonListItems: DentalBidirectionalListItem[] = [];
  public sortedReprocessReasonListItems: DentalBidirectionalListItem[] = [];

  public urgentOnly = false;

  constructor(
    private service: AppService,
    private alert: AlertService,
    private spinner: LoadingService,
    private dialog: MatDialog,
    private commonFunctions: CommonFunctions,
    private datePipe: DatePipe,
    private referralLockService: ReferralLockService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    this.loadLists();

    // Reset the dropdown after the action is performed
    this.currentActionValue = '';

    this.getDentalReferrals().subscribe(() => {
      this.referrals.forEach(referral => {
        if (!referral.review) {
          referral.review = new DentalReferralFormReview();
        }
      });

      // Update the data source with the referrals
      //this.referralsDataSource.data = this.referrals;

      //this.applyClosedFilter(); // Apply the closed filter initially

      this.referralsDataSource.filterPredicate = (data: DentalReferralForm, combinedFilter: string) => {
        // ---------- Safe parse ----------
        let obj: { searchText?: string; filterSelection?: string[]; urgentFlag?: number } = {};
        if (combinedFilter && combinedFilter.trim().length) {
          try {
            obj = JSON.parse(combinedFilter);
          } catch {
            // if malformed, treat as no filters
            obj = {};
          }
        }

        const normalizedSearch = (obj.searchText || '').toLowerCase().trim();
        const numericSearch = normalizedSearch.replace(/\D/g, '');
        const isNumericOnly = /^[0-9]+$/.test(normalizedSearch);

        const normalizedPhone = (data.memberPhoneNumber || '').replace(/\D/g, '');
        let passesSearch = true;

        // ---------- Search ----------
        if (normalizedSearch.length >= 3) {
          if (isNumericOnly || /[\(\)\-\s]/.test(normalizedSearch)) {
            passesSearch = normalizedPhone.includes(numericSearch);
          } else {
            passesSearch =
              (data.memberMedicaidId?.toLowerCase().includes(normalizedSearch) ?? false) ||
              (data.memberFirstName?.toLowerCase().includes(normalizedSearch) ?? false) ||
              (data.memberLastName?.toLowerCase().includes(normalizedSearch) ?? false) ||
              (data.review?.dcoNote?.toLowerCase().includes(normalizedSearch) ?? false) ||
              ((data.reprocessReason ?? '').toLowerCase().includes(normalizedSearch));
          }
        }

        // ---------- Multi-select dropdown (OR across selected tokens) ----------
        const selections: string[] = Array.isArray(obj.filterSelection) ? obj.filterSelection : [];

        const tokenMatches = (token: string): boolean => {
          if (!token) return true;

          // DCO Note
          if (token.startsWith('NOTE:')) {
            const noteVal = token.substring(5);
            const current = data.review?.dcoNote ?? '';
            return current === noteVal;            
          }

          
          if (token.startsWith('REPROC:')) {
            const reasonVal = token.substring(7);
            const current = data.reprocessReason ?? '';
            return current === reasonVal;
          }

          // Status
          if (token === 'STATUS:CLOSED') return data.review?.isClosed === true;
          if (token === 'STATUS:OPEN') return data.review?.isClosed !== true;

          // Existing
          switch (token) {
            case 'PCP': return (data as any).referralType === 'PCP';
            case 'HRAT': return (data as any).referralType === 'HRAT';
            case 'Maternity': return (data as any).referralType === 'Maternity';
            case 'HasSpokeWith': return data.review?.spokeWithMember === true;
            case 'HasLeftMessage': return data.review?.leftMessage === true;
            case 'HasSentLetter': return data.review?.sentLetter === true;
            case 'HasScheduledAppointment': return data.review?.scheduledAppointment === true;
            case 'HasNotes': return !!(data.review?.dcoNote && data.review?.dcoNote.trim().length > 0);
            default: return true;
          }
        };

        const passesDropdown =
          selections.length === 0 ? true : selections.some(token => tokenMatches(token));

        // ---------- (Optional) urgent flag ----------
        const wantUrgentOnly = obj.urgentFlag === 1;
        const passesUrgent = !wantUrgentOnly || this.isUrgent(data);

        return passesSearch && passesDropdown && passesUrgent;
      };


      // Clear any existing selection to ensure no rows are selected on load
      this.selection.clear();

    });
  }

  ngAfterViewInit() {
    this.paginator.pageSize = 10; //set default row count
    this.referralsDataSource.paginator = this.paginator;

    this.referralsDataSource.sort = this.sort;
  }

  private getDentalReferrals(): Observable<DentalReferralForm[]> {
    const request = new GetDentalReferralsToReprocessRequest();
    request.includeClosedReferrals = this.showClosed;
    
    // Show the spinner before the request starts
    this.spinner.show();

    return this.service.GetDentalReferralsToReprocess(request).pipe(
      map((result: GetDentalReferralsToReprocessResponse) => {

        if (result.success) {
          const sortedReferrals = result.referrals.sort((a, b) => {
            const dateA = a.receivedDate ? new Date(a.receivedDate).getTime() : 0;
            const dateB = b.receivedDate ? new Date(b.receivedDate).getTime() : 0;
            return dateB - dateA; // Descending
          });
          this.referrals = sortedReferrals;

          this.referrals.forEach(referral => {
            if (!referral.review) {
              referral.review = new DentalReferralFormReview(); // Initialize as an empty object if undefined
            }
          });
          
          this.referralsDataSource.data = this.referrals;
          
          return result.referrals; // Return the DCO array
        } else {
          console.log(`ERROR: ${result.message}`);
          return []; // Return an empty array in case of failure
        }
      }),
      catchError((error) => {
        console.error(error);
        return of([]); // Return an empty array or appropriate fallback in case of error
      }),
      finalize(() => {
        // Hide the spinner after the request completes
        this.spinner.hide();
      })
    );
  }

  onAction(action: string) {
    const selectedRows = this.selection.selected;
    if (selectedRows.length === 0) {
      this.alert.warning('No rows selected.');
      this.resetActionDropdown();
      return;
    }

    // Call a method or handle logic based on the selected action
    switch (action) {
      case 'Export':
        //this.performExport(selectedRows);
        this.openExportDialog('selected');
        break;
      case 'Close':
        this.performClose(selectedRows);
        break;
      case 'Open':
        this.performOpen(selectedRows);
        break;
    }

    this.resetActionDropdown();

  }

  private resetActionDropdown(): void {
    setTimeout(() => this.currentActionValue = '', 1);
  }

  toggleShowClosed(): void {
    this.showClosed = !this.showClosed;
    this.reloadDentalReferralGrid();
  }

  reloadDentalReferralGrid() {
    this.getDentalReferrals().subscribe(() => {
      this.referrals.forEach(referral => {
        if (!referral.review) {
          referral.review = new DentalReferralFormReview();
        }
      });
      if (this.sort && this.sort.active && this.sort.direction) {
        // Reapply sorting based on the current active sort state
        this.onReferralsGridSortChange({
          active: this.sort.active,
          direction: this.sort.direction
        });
      } else {
        this.referralsDataSource.data = this.referrals;
      }
    });
  }
  

  performExport(referrals: DentalReferralForm[]) {
    // Flatten and prepare the data for export
    const dataToExport = referrals.map(referral => {
      // Destructure to separate review from the main referral properties
      const { review, ...referralData } = referral;
      const flatData: any = { ...referralData }; // Start with all top-level fields of DentalReferralForm

      if (review) {
        // Add review fields to the flatData object
        Object.entries(review).forEach(([key, value]) => {
          if (key === 'notes') {
            // notes is an array of DentalReferralFormReviewNote; store as JSON string
            flatData[`review_notes`] = JSON.stringify(value || []);
          } else {
            // Prefix review fields with 'review_' to differentiate them
            flatData[`review_${key}`] = value;
          }
        });
      } else {
        // If no review is present, still include empty notes field
        flatData[`review_notes`] = JSON.stringify([]);
      }

      return flatData;
    });

    // Create a worksheet from the flattened data
    const worksheet = XLSX.utils.json_to_sheet(dataToExport);

    // Create a new workbook and append the worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Referrals');

    // Generate a binary array for the workbook
    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });

    // Create a Blob and use FileSaver to prompt the download
    const blob = new Blob([wbout], { type: 'application/octet-stream' });
    saveAs(blob, `referrals_export_${new Date().toISOString()}.xlsx`);
  }


  performClose(referrals: DentalReferralForm[]) {
    //console.log('Closing referrals:', referrals);

    //check for locked referrals
    const lockedReferrals = referrals.filter(referral => referral.review?.isLocked);
    if (lockedReferrals.length > 0) {
      this.alert.warning('One or more selected referrals are locked and cannot be closed.');
      this.resetActionDropdown();
      return;
    }

    //check for referrals without appointment dates
    const referralsWithoutDates = referrals.filter(referral => !referral.review?.appointmentDate);
    if (referralsWithoutDates.length > 0) {
      // Step 2: Prompt the user for confirmation
      const confirmMessage = `
      Some referrals do not have an appointment date.
      Are you sure you want to close them anyway?
    `;
      const userConfirmed = window.confirm(confirmMessage);

      if (!userConfirmed) {
        this.resetActionDropdown();
        return; // Stop execution if the user does not confirm
      }
    }

    //referrals.forEach(referral => {
    //  if (!referral.review) {
    //    referral.review = new DentalReferralFormReview(); // Ensure the review object exists
    //  }
    //  if (!referral.review.referralType) {
    //    referral.review.referralId = referral.id;
    //    referral.review.referralType = referral.referralType;
    //    referral.review.isClosed = true;
    //  }
    //});

    referrals.forEach(referral => {
      if (!referral.review || !referral.review.id) {
        referral.review = new DentalReferralFormReview();
        referral.review.referralId = referral.id;
        referral.review.referralType = referral.referralType;
      }

      referral.review.isClosed = true;
    });

    this.spinner.show();
    const request = new SaveDentalReferralReviewsRequest();
    request.reviews = referrals.map(referral => referral.review);

    this.service.SaveDentalReferralReviews(request)
      .pipe(
        finalize(() => {
          // Hide the spinner after the request completes
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.alert.success('Referrals updated successfully!');
            this.ngOnInit();

          } else {
            this.alert.warning(response.message);
          }
        },
        error: (error) => {
          console.error('Error submitting referrals:', error);
          this.alert.error('Failed to submit referrals. Please try again.');
        }
      });

  }

  performOpen(referrals: DentalReferralForm[]) {
    //console.log('Opening referrals:', referrals);
    //referrals.forEach(referral => {
    //  if (!referral.review) {
    //    referral.review = new DentalReferralFormReview(); // Ensure the review object exists
    //  }
    //  if (!referral.review.referralType) {
    //    referral.review.referralId = referral.id;
    //    referral.review.referralType = referral.referralType;
    //    referral.review.isClosed = false;
    //  }
    //});

    referrals.forEach(referral => {
      if (!referral.review || !referral.review.id) {
        referral.review = new DentalReferralFormReview();
        referral.review.referralId = referral.id;
        referral.review.referralType = referral.referralType;
      }

      referral.review.isClosed = false;
    });

    this.spinner.show();
    const request = new SaveDentalReferralReviewsRequest();
    request.reviews = referrals.map(referral => referral.review);

    //console.log(request);

    this.service.SaveDentalReferralReviews(request)
      .pipe(
        finalize(() => {
          // Hide the spinner after the request completes
          this.spinner.hide();
        })
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.alert.success('Referrals updated successfully!');
            //this.getDentalReferrals();
            //this.selection.clear();
            this.ngOnInit();
          } else {
            this.alert.warning(response.message);
          }
        },
        error: (error) => {
          console.error('Error submitting referrals:', error);
          this.alert.error('Failed to submit referrals. Please try again.');
        }
      });
  }

  onSearch() {
    this.applyFilter();
  }

  onFilterChange() {
    this.applyFilter();
  }

  applyFilter() {
    if (!this.filtersEnabled) {
      // Use a minimal valid JSON so predicate's JSON.parse succeeds safely
      this.referralsDataSource.filter = JSON.stringify({});
      return;
    }

    const filterObject = {
      searchText: this.currentSearchValue,
      filterSelection: this.currentFilterSelection, // array
      urgentFlag: this.urgentOnly ? 1 : 0
    };

    this.referralsDataSource.filter = JSON.stringify(filterObject);
    this.clampSelectionToFilter();
  }

  clearSearch() {
    this.currentSearchValue = '';
    this.applyFilter();
  }

  clearFilter() {
    this.currentFilterSelection = [];
    this.applyFilter();
  }

  resetAllFilters() {
    this.currentSearchValue = '';
    this.currentFilterSelection = [];
    this.applyFilter();
  }
  
  hasActiveFilters(): boolean {
    return (this.currentSearchValue && this.currentSearchValue.length >= 3)
      || (this.currentFilterSelection?.length > 0);
  }


  // This method can translate the filter keys into more user-friendly labels if needed
  displayFilterName(filterValue: string): string {
    if (!filterValue) return '';
    if (filterValue.startsWith('NOTE:')) return `DCO Note: ${filterValue.substring(5)}`;
    if (filterValue.startsWith('REPROC:')) return `Reprocess Reason: ${filterValue.substring(7)}`;
    if (filterValue === 'STATUS:CLOSED') return 'Closed Only';
    if (filterValue === 'STATUS:OPEN') return 'Open Only';
    switch (filterValue) {
      case 'PCP': return 'PCP Only';
      case 'HRAT': return 'HRAT Only';
      case 'Maternity': return 'Maternity Only';
      case 'HasSpokeWith': return 'Has Spoke With';
      case 'HasLeftMessage': return 'Has Left Message';
      case 'HasSentLetter': return 'Has Sent Letter';
      case 'HasScheduledAppointment': return 'Has Scheduled Appointment';
      case 'HasNotes': return 'Has Notes';
      default: return filterValue;
    }
  }

  private loadLists() {
    const request = new GetDentalBidirectionalListsRequest();

    // Show the spinner before the request starts
    this.spinner.show();
    this.service.GetDentalBidirectionalLists(request).pipe(
      tap((result) => {
        if (result.success) {
          this.reprocessReasonListItems = result.lists
            .find(l => l.name === 'ReprocessReasons')
            .listItems
            .filter(i => i.active === true);

          this.dcoNotesListItems = result.lists.find(l => l.name == 'DCONotes').listItems.filter(i => i.active == true);

          // Sort them by the `order` property
          this.sortedReprocessReasonListItems = [...this.reprocessReasonListItems]
            .sort((a, b) => a.order - b.order);
        } else {
          console.log(`ERROR: ${result.message}`);
        }
      }),
      finalize(() => this.spinner.hide())
    ).subscribe();
  }

  // Method to handle edit action
  onEdit(referral: DentalReferralForm, index: number) {
    this.activateRow(referral, index);
  }

  activateRow(referral: DentalReferralForm, index: number) {
    if (referral.review?.isClosed) {
      // Prevent activation if the referral is closed
      return;
    }

    if (!this.selectedReferral) {
      this.selectedReferral = JSON.parse(JSON.stringify(referral));
      //this.activeRow = this.activeRow === index ? null : index; // Toggle the active row
    }
  }

  // Master toggle to select or deselect all rows
  masterToggle() {
    const filtered = this.getFilteredRows();
    const allSelected = filtered.length > 0 && filtered.every(r => this.selection.isSelected(r));

    if (allSelected) {
      // Deselect only filtered rows
      filtered.forEach(r => this.selection.deselect(r));
    } else {
      // Select only filtered rows
      filtered.forEach(r => this.selection.select(r));
    }
  }

  isAllSelected(): boolean {
    const filtered = this.getFilteredRows(); // what the user actually sees
    if (filtered.length === 0) {
      return false;
    }
    return filtered.every(r => this.selection.isSelected(r));
  }

  isIndeterminate(): boolean {
    const filtered = this.getFilteredRows();
    if (filtered.length === 0) return false;
    const selectedInFiltered = filtered.filter(r => this.selection.isSelected(r)).length;
    return selectedInFiltered > 0 && selectedInFiltered < filtered.length;
  }

  // Check if the master checkbox should be checked
  checkboxLabel(row?: DentalReferralForm): string {
    if (!row) {
      return `${this.isAllSelected() ? 'deselect' : 'select'} all`;
    }
    return `${this.selection.isSelected(row) ? 'deselect' : 'select'} row ${row.memberMedicaidId}`;
  }

  onRecheck(referral: DentalReferralForm, index: number) {
    const request = {
      referralId: referral.id,
      referralType: referral.referralType
    };

    this.service.GetReferralByReferralId(request).subscribe({
      next: (response) => {
        if (!response.success || !response.referral) {
          this.alert.warning('Unable to retrieve the latest referral details.');
          return;
        }

        const currentUserId = this.authService.getUserId()?.toLowerCase();
        const lockedByUserId = response.referral.review?.lockedByUserId?.toLowerCase();

        // Case 1: Already locked by another user
        if (response.referral.review?.isLocked && currentUserId !== lockedByUserId) {
          const lockedBy = response.referral.review?.lockedByUser?.fullName ?? 'another user';
          const formattedDate = this.datePipe.transform(response.referral.review?.lockedDate, 'MM/dd/yyyy h:mm a');
          this.alert.warning(`This referral was locked by ${lockedBy} on ${formattedDate}.`);
          this.updateReferralInGrid(referral.id, response.referral);
          this.openReferralDetailsDialog(response.referral, true);
          return;
        }

        // Case 2: Already locked by current user — allow access
        if (response.referral.review?.isLocked && currentUserId === lockedByUserId) {
          this.updateReferralInGrid(referral.id, response.referral);
          this.openReferralDetailsDialog(response.referral, false);
          return;
        }

        // Case 3: Not locked — try to lock it
        const lockRequest = {
          referralId: referral.id,
          referralType: referral.referralType
        };

        this.referralLockService.LockReferral(lockRequest).subscribe({
          next: (lockResponse) => {
            if (lockResponse.success && lockResponse.referral) {
              this.updateReferralInGrid(referral.id, lockResponse.referral);
              this.openReferralDetailsDialog(lockResponse.referral, false);
            } else {
              this.alert.warning(lockResponse.message || 'Unable to lock the referral.');
            }
          },
          error: () => {
            this.alert.error('An error occurred while trying to lock the referral.');
          }
        });
      },
      error: (err) => {
        console.error('Error fetching referral:', err);
        this.alert.error('An error occurred while fetching referral details.');
      }
    });
  }


  private updateReferralInGrid(referralId: number, updatedReferral: DentalReferralForm): void {
    const index = this.referrals.findIndex(r => r.id === referralId);
    if (index !== -1) {
      this.referrals[index] = updatedReferral;

      // Reassign to trigger change detection
      this.referralsDataSource.data = [...this.referrals];

      // Reapply current sort state (manually)
      if (this.sort?.active && this.sort.direction) {
        this.onReferralsGridSortChange(this.sort);
      }
    }
  }



  onResubmit(referral: DentalReferralForm, index: number) {
        
    this.spinner.show();

    //get the latest version of the referral
    const getReferralByReferralIdRequest = {
      referralId: referral.id,
      referralType: referral.referralType
    };

    this.service.GetReferralByReferralId(getReferralByReferralIdRequest).subscribe({
      next: (res) => {
        if (res.success && res.referral) {

          //check if locked by another user
          if (res.referral.review?.isLocked) {
            const lockedBy = res.referral.review?.lockedByUser?.fullName;
            const formattedDate = this.datePipe.transform(res.referral.review?.lockedDate, 'MM/dd/yyyy h:mm a');
            this.alert.warning(`This referral cannot be resubmitted because it is currently locked by ${lockedBy} on ${formattedDate}.`);
            this.spinner.hide();
            return;
          }


          const request = new ReprocessReferralRequest();
          request.referral = res.referral;
          if (!request.referral.review?.referralType) {
            request.referral.review.referralType = request.referral.referralType;
          }


          console.log(res.referral);
          this.service.ReprocessReferral(request)
            .pipe(
              finalize(() => {
                // Hide the spinner after the request completes
                this.spinner.hide();
              })
            )
            .subscribe({
              next: (response) => {
                if (response.success) {

                  if (!response.isMemberFound) {
                    this.alert.warning('Member not found');
                  } else if (!response.isMemberActive) {
                    this.alert.warning('Member not active');
                  } else if (response.memberDCO) {
                    this.alert.success(`Referral sent to ${response.memberDCO?.displayName} for reprocessing.`);

                    this.reloadDentalReferralGrid();
                  }

                } else {
                  this.alert.warning(response.message);
                }
              },
              error: (error) => {
                console.error('Error submitting reprocessing request:', error);
                this.alert.error('Failed to submit reprocessing request. Please try again.');
              }
            });
        } else {
          this.alert.warning('Unable to retrieve the latest referral details.');
        }
      },
      error: (err) => {
        console.error('Error fetching referral:', err);
        this.alert.error('An error occurred while fetching referral details.');
      }
    });




  }

  // Method to handle info action
  //onInfo(referral: DentalReferralForm, index: number) {
  //  this.openReferralDetailsDialog(referral);
  //}

  // Method to handle save action
  onSave(referral: DentalReferralForm, index: number) {

  }

  // Method to handle cancel action
  onCancel(referral: DentalReferralForm, index: number) {
    if (this.selectedReferral) {
      const data = this.referralsDataSource.data;
      // Replace the current row with a deep copy of the original referral
      data[index] = JSON.parse(JSON.stringify(this.selectedReferral));
      this.referralsDataSource.data = [...data]; // Reassign to trigger change detection
    }

    this.selectedReferral = null;
    //this.activeRow = null;
  }

  removeFilterValue(value: string) {
    const index = this.currentFilterSelection.indexOf(value);
    if (index >= 0) {
      this.currentFilterSelection.splice(index, 1);
      this.applyFilter();
    }
  }

  openReferralDetailsDialog(referral: DentalReferralForm, doNotUnlock: boolean) {
    const dialogRef = this.dialog.open(DcoReferralDetailsDialogComponent, {
      data: { referral: referral, showMemberEnrollmentHistory: true, allowMemberUpdates: true, showReferralActivitySection: true, showMemberReferralHistory: true },
      panelClass: 'custom-dialog-panel',
      height: '90vh',
      disableClose: true,
    });

    dialogRef.afterClosed().subscribe(response => {

      if (!doNotUnlock) {
        // Attempt to unlock the referral
        const unlockRequest = {
          referralId: referral.id,
          referralType: referral.referralType
        };

        this.referralLockService.UnlockReferral(unlockRequest).subscribe({
          next: (unlockResponse) => {
            if (unlockResponse.success && unlockResponse.referral) {
              this.updateReferralInGrid(referral.id, unlockResponse.referral);
            } else {
              console.warn('Unlock failed or referral not returned.');
            }
          },
          error: (err) => {
            console.error('Error unlocking referral:', err);
          }
        });
      }

      // Refresh grid if necessary
      if (response?.data?.refresh) {
        this.reloadDentalReferralGrid();
      }
    });
  }


  public onReferralsGridSortChange(sortState: Sort) {
    if (!this.referrals || !Array.isArray(this.referrals)) {
      console.error('referrals is not defined or is not an array.');
      return;
    }
    // If there's no active sort or direction is empty, reset data
    if (!sortState.active || sortState.direction === '') {
      this.referralsDataSource.data = this.referrals;
      return;
    }

    const sortedData = this.referrals.slice().sort((a, b) => {
      const isAsc = sortState.direction === 'asc';
      switch (sortState.active) {
        case 'memberId':
          return this.commonFunctions.compare(a.memberMedicaidId, b.memberMedicaidId, isAsc);
        case 'memberName':
          return this.commonFunctions.compare(a.memberFirstName, b.memberFirstName, isAsc);
        case 'receivedDate':
          return this.commonFunctions.compare(new Date(a.receivedDate), new Date(b.receivedDate), isAsc);
        case 'source':
          return this.commonFunctions.compare(a.referralType, b.referralType, isAsc);
        case 'reprocessReason':
          return this.commonFunctions.compare(a.reprocessReason, b.reprocessReason, isAsc);
        case 'locked':
          return this.commonFunctions.compare(a.review?.isLocked, b.review?.isLocked, isAsc);
        case 'dcoNote':
          return this.commonFunctions.compare(a.review?.dcoNote, b.review?.dcoNote, isAsc);
        default:
          return 0;
      }
    });

    if (!sortedData) {
      console.error('sortedData is undefined after sorting');
      return;
    }

    // Update the data source with a new array reference to trigger change detection
    this.referralsDataSource.data = [...sortedData];
  }

  canForceUnlock(referral: DentalReferralForm): boolean {
    const currentUserId = this.authService.getUserId()?.toLowerCase();
    const lockedByUserId = referral.review?.lockedByUserId?.toLowerCase();

    // Allow if current user is the one who locked the referral
    if (currentUserId && lockedByUserId && currentUserId === lockedByUserId) {
      return true;
    }

    // Or if user has Admin/SuperAdmin role
    return this.authService.getCanForceUnlock();
  }

  getLockTooltip(referral: DentalReferralForm, index: number): string {

    if (this.selectedReferral?.id === referral.id) {
      return 'This referral is currently being edited and cannot be unlocked.';
    } else {

      const currentUserId = this.authService.getUserId()?.toLowerCase();
      const lockedByUserId = referral.review?.lockedByUserId?.toLowerCase();
      const lockedBy = referral.review?.lockedByUser?.fullName ?? 'another user';
      const lockedDate = this.datePipe.transform(referral.review?.lockedDate, 'MM/dd/yyyy h:mm a');

      const isLockedByCurrentUser = currentUserId && lockedByUserId && currentUserId === lockedByUserId;

      if (isLockedByCurrentUser) {
        return `Click to unlock\nYou locked this referral on ${lockedDate}`;
      } else if (this.authService.getCanForceUnlock()) {
        return `Click to force unlock\nLocked by ${lockedBy} on ${lockedDate}`;
      } else {
        return `Locked by ${lockedBy} on ${lockedDate}`;
      }
    }
  }


  onLockClick(referral: DentalReferralForm, index: number): void {
    if (!this.canForceUnlock(referral)) return;
    if (this.selectedReferral?.id === referral.id) return;

    this.forceUnlock(referral);
  }


  forceUnlock(referral: DentalReferralForm): void {
    const currentUserId = this.authService.getUserId()?.toLowerCase();
    const lockedByUserId = referral.review?.lockedByUserId?.toLowerCase();
    const lockedByName = referral.review?.lockedByUser?.fullName ?? 'another user';

    const isSelfUnlock = currentUserId === lockedByUserId;

    const title = isSelfUnlock ? 'Unlock Referral' : 'Force Unlock';
    const message = isSelfUnlock
      ? `You locked this referral on ${this.datePipe.transform(referral.review?.lockedDate, 'MM/dd/yyyy h:mm a')}.\nDo you want to unlock it?`
      : `This referral is currently locked by ${lockedByName}.\nAre you sure you want to force unlock it?`;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: title,
        message: message
      }
    });

    dialogRef.afterClosed().subscribe(confirmed => {
      if (!confirmed) return;

      const request = {
        referralId: referral.id,
        referralType: referral.referralType
      };

      this.referralLockService.UnlockReferral(request).subscribe({
        next: (res) => {
          if (res.success && res.referral) {
            const index = this.referrals.findIndex(r => r.id === referral.id);
            if (index !== -1) {
              this.referrals[index] = res.referral;
              this.referralsDataSource.data = [...this.referrals];
            }
            const successMsg = isSelfUnlock
              ? 'Referral successfully unlocked.'
              : 'Referral successfully force-unlocked.';
            this.alert.success(successMsg);
          } else {
            this.alert.warning(res.message || 'Unable to unlock the referral.');
          }
        },
        error: (err) => {
          console.error('Error unlocking referral:', err);
          this.alert.error('An error occurred while unlocking the referral.');
        }
      });
    });
  }

  openExportDialog(scope: 'selected' | 'all' = 'selected') {
    // use selected rows if any and scope == 'selected', else use full set
    let baseRows: DentalReferralForm[];

    if (scope === 'selected') {
      const selectedVisible = this.getSelectedFilteredRows();
      if (!selectedVisible.length) {
        this.alert.warning('No rows selected.');
        return;
      }
      baseRows = selectedVisible;
    } else {
      // 'all' → send the whole dataset
      baseRows = this.referrals;
    }

    const data: ExportDialogData = {
      title: 'Export Referrals',
      rows: baseRows,                       // base rows (selected or all)
      dataSource: this.referralsDataSource, // lets dialog honor current filters
      matSort: this.sort,                   // lets dialog honor current sort
      flatten: true,                        // expose review.* as columns
      //restrictToInputRows: scope === 'selected',
      identityKey: 'id',
      // nice default column order (users can still re-order in the dialog)
      presetColumns: [
        //default order

        
        { key: 'receivedDate', header: 'receivedDate' },
        { key: 'importDate', header: 'importDate' },
        { key: 'referralDate', header: 'referralDate' },
        { key: 'cco', header: 'cco' },
        { key: 'memberMedicaidId', header: 'memberMedicaidId' },
        { key: 'memberFirstName', header: 'memberFirstName' },
        { key: 'memberLastName', header: 'memberLastName' },
        { key: 'memberParentOrGuardianName', header: 'memberParentOrGuardianName' },
        { key: 'memberPhoneNumber', header: 'memberPhoneNumber' },
        { key: 'memberDob', header: 'memberDob' },
        { key: 'memberLanguageSpoken', header: 'memberLanguageSpoken' },

        { key: '$type', header: '$type' },
        { key: 'isUrgent', header: 'isUrgent' },
        { key: 'clinicProgramName', header: 'clinicProgramName' },
        { key: 'clinicNpi', header: 'clinicNpi' },
        { key: 'providerName', header: 'providerName' },
        { key: 'providerPhoneNumber', header: 'providerPhoneNumber' },
        { key: 'providerFaxNumber', header: 'providerFaxNumber' },
        { key: 'providerEmailAddress', header: 'providerEmailAddress' },
        { key: 'submittedBy', header: 'submittedBy' },
        { key: 'dentalCariesOrDecay', header: 'dentalCariesOrDecay' },
        { key: 'swellingOrAbscess', header: 'swellingOrAbscess' },
        { key: 'oralPathology', header: 'oralPathology' },
        { key: 'pain', header: 'pain' },
        { key: 'otherClinicalPresentation', header: 'otherClinicalPresentation' },
        { key: 'otherClinicalPresentationDescription', header: 'otherClinicalPresentationDescription' },
        { key: 'pregnancy', header: 'pregnancy' },
        { key: 'diabetes', header: 'diabetes' },
        { key: 'cardiovascularDisease', header: 'cardiovascularDisease' },
        { key: 'otherMedicalConditions', header: 'otherMedicalConditions' },
        { key: 'otherMedicalConditionsDescription', header: 'otherMedicalConditionsDescription' },
        { key: 'commentsAboutCondition', header: 'commentsAboutCondition' },
        { key: 'memberMailingAddress1', header: 'memberMailingAddress1' },
        { key: 'memberMailingAddress2', header: 'memberMailingAddress2' },
        { key: 'memberMailingCity', header: 'memberMailingCity' },
        { key: 'memberMailingState', header: 'memberMailingState' },
        { key: 'memberMailingZip', header: 'memberMailingZip' },
        { key: 'memberMailingCounty', header: 'memberMailingCounty' },
        { key: 'memberLanguageWritten', header: 'memberLanguageWritten' },
        { key: 'referralType', header: 'referralType' },
        { key: 'submittedOnDate', header: 'submittedOnDate' },
        { key: 'reprocess', header: 'reprocess' },
        { key: 'reprocessReason', header: 'reprocessReason' },
        { key: 'reprocessAdditionalDetails', header: 'reprocessAdditionalDetails' },
        { key: 'review.referralId', header: 'review_referralId' },
        { key: 'review.spokeWithMember', header: 'review_spokeWithMember' },
        { key: 'review.leftMessage', header: 'review_leftMessage' },
        { key: 'review.sentLetter', header: 'review_sentLetter' },
        { key: 'review.scheduledAppointment', header: 'review_scheduledAppointment' },
        { key: 'review.appointmentDate', header: 'review_appointmentDate' },
        { key: 'review.dcoNote', header: 'review_dcoNote' },
        { key: 'review.isLocked', header: 'review_isLocked' },
        { key: 'review.lockedDate', header: 'review_lockedDate' },
        { key: 'review.isClosed', header: 'review_isClosed' },
        { key: 'review.referralType', header: 'review_referralType' },
        { key: 'review.notes', header: 'review_notes' },
        { key: 'providerHtrioId', header: 'providerHtrioId' },
        { key: 'importedDco', header: 'importedDco' },
        { key: 'referralReason', header: 'referralReason' },
        { key: 'additionalNotes', header: 'additionalNotes' },
        { key: 'clinic', header: 'clinic' },
        { key: 'questions', header: 'questions' },
        { key: 'providerNpi', header: 'providerNpi' },
        { key: 'patientAwareOfRequest', header: 'patientAwareOfRequest' },
        { key: 'swellingOrAbsess', header: 'swellingOrAbsess' },
        { key: 'comments', header: 'comments' },
        { key: 'dentalCarriesOrDecay', header: 'dentalCarriesOrDecay' },
        { key: 'edd', header: 'edd' },
        { key: 'hasOtherCondition', header: 'hasOtherCondition' },
        { key: 'otherConditions', header: 'otherConditions' },
        { key: 'hasOtherPresentation', header: 'hasOtherPresentation' },
        { key: 'otherPresentation', header: 'otherPresentation' },
        { key: 'dco.dcoName', header: 'dcoName' },


        { key: 'review.lockedByUser', header: 'review_lockedByUser' },
        { key: 'review.lockedByUserId', header: 'review_lockedByUserId' },
        { key: 'review.id', header: 'review_id' },
        { key: 'dentalCareOrganizationId', header: 'dentalCareOrganizationId' },
        //{ key: 'uniqueId', header: 'uniqueId' },
        { key: 'createdByUserId', header: 'createdByUserId' },
        { key: 'createdDate', header: 'createdDate' },
        { key: 'modifiedByUserId', header: 'modifiedByUserId' },
        { key: 'modifiedDate', header: 'modifiedDate' },
        { key: 'id', header: 'id' },
        { key: 'review.createdByUserId', header: 'review_createdByUserId' },
        { key: 'review.createdDate', header: 'review_createdDate' },
        { key: 'review.modifiedByUserId', header: 'review_modifiedByUserId' },
        { key: 'review.modifiedDate', header: 'review_modifiedDate' },
      ],
      // override headers for any other auto-detected fields
      headerOverrides: {
        memberParentOrGuardianName: 'Parent/Guardian'
      },
      // quick formatters for export file
      accessors: {
        memberPhoneNumber: (r: any) => {
          const digits = (r?.memberPhoneNumber || '').replace(/\D/g, '');
          return digits.length === 10
            ? digits.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
            : r?.memberPhoneNumber ?? '';
        },
        submittedOnDate: (r: any) => r?.submittedOnDate
          ? new Date(r.submittedOnDate).toLocaleString()
          : '',
        receivedDate: (r: any) => r?.receivedDate
          ? new Date(r.receivedDate).toLocaleString()
          : '',
        'review.appointmentDate': (r: any) => r?.review?.appointmentDate
          ? new Date(r.review.appointmentDate).toLocaleDateString()
          : ''
      },
      // optionally hide noisy fields if they appear
      excludeKeys: ['review.notes', 'lockedByUser', 'lockedByUserId']
    };

    const ref = this.dialog.open(ExportDialogComponent, {
      width: '65vw',
      maxWidth: '96vw',
      height: '90vh',
      maxHeight: '90vh',
      panelClass: 'dbd-export-dialog',
      disableClose: true,
      data
    });

    ref.keydownEvents().subscribe(e => {
      if (e.key === 'Escape') ref.close();
    })
  }

  private getSelectedFilteredRows(): DentalReferralForm[] {
    const visibleIds = new Set(this.getFilteredRows().map(r => r.id));
    return this.selection.selected.filter(r => visibleIds.has(r.id));
  }

  private getFilteredRows(): DentalReferralForm[] {
    // MatTableDataSource always maintains filteredData (even when no filter)
    return this.referralsDataSource.filteredData ?? this.referralsDataSource.data ?? [];
  }

  filteredCount(): number {
    return this.getFilteredRows().length;
  }

  selectedInFilteredCount(): number {
    const ids = new Set(this.getFilteredRows().map(r => r.id));
    return this.selection.selected.reduce((acc, r) => acc + (ids.has(r.id) ? 1 : 0), 0);
  }

  private clampSelectionToFilter(): void {
    const allowedIds = new Set(this.getFilteredRows().map(r => r.id));
    // Deselect anything that isn't in the current filtered view
    this.selection.selected.slice().forEach(r => {
      if (!allowedIds.has(r.id)) this.selection.deselect(r);
    });
  }

  private getCurrentPageRows(): DentalReferralForm[] {
    const rows = this.getFilteredRows();
    const size = this.paginator?.pageSize ?? rows.length;
    const index = this.paginator?.pageIndex ?? 0;
    const start = index * size;
    const end = start + size;
    return rows.slice(start, end);
  }

  pageCount(): number {
    return this.getCurrentPageRows().length;
  }

  selectedOnPageCount(): number {
    const ids = new Set(this.getCurrentPageRows().map(r => r.id));
    return this.selection.selected.reduce((acc, r) => acc + (ids.has(r.id) ? 1 : 0), 0);
  }

  private asPCP(r: DentalReferralForm): r is PCPReferral {
    return r?.referralType === 'PCP';
  }

  public isUrgent(r: DentalReferralForm): boolean {
    if (!this.asPCP(r)) return false;
    // normalize in case backend sent "true"/1
    const v: any = (r as PCPReferral).isUrgent;
    return v === true || v === 'true' || v === 1 || v === '1';
  }

  public get urgentCount(): number {
    return (this.referrals ?? []).filter(r => this.isUrgent(r)).length;
  }

  public toggleUrgentOnly(): void {
    this.urgentOnly = !this.urgentOnly;
    this.applyFilter(); // will rebuild the combined string (see below)
  }
}
